import { useCallback } from 'react';
import {
  getInterviewFeedbacksAPI,
  getInterviews,
  improveInterviewFeedbackComment,
  saveInterviewFeedbacks,
} from 'actions';
import type {
  ImproveInterviewFeedbackCommentPayload,
  ImproveInterviewFeedbackCommentResponse,
} from 'actions/jobActions';
import { withFeedbackInterview } from 'constants/interview';
import { useAppDispatch } from 'store';
import type { Uuid } from 'store/reducers/types';
import { formValuesMapper, getPostFeedbackBody } from './utils';
import type { FeedbackData, FeedbackFormValues } from './types';

interface UseInterviewFeedbackActionsParams {
  candidateId: string;
  interviewId?: Uuid;
  jobId: string;
  onFeedbackChanged?: () => void;
  setEditUuid: (uuid: string) => void;
  stepId: Uuid;
}

export const useInterviewFeedbackActions = ({
  candidateId,
  interviewId,
  jobId,
  onFeedbackChanged,
  setEditUuid,
  stepId,
}: UseInterviewFeedbackActionsParams) => {
  const dispatch = useAppDispatch();

  const refreshFeedbacks = useCallback(() => {
    dispatch(getInterviews(jobId, stepId, candidateId));
    dispatch(getInterviewFeedbacksAPI(jobId, stepId, candidateId));
    onFeedbackChanged?.();
  }, [candidateId, dispatch, jobId, onFeedbackChanged, stepId]);

  const onSaveScorecard = useCallback(
    (values: FeedbackFormValues, feedbackId: Uuid) => {
      saveInterviewFeedbacks(
        jobId,
        stepId,
        candidateId,
        feedbackId,
        withFeedbackInterview(formValuesMapper(values), interviewId)
      ).then(() => {
        setEditUuid('');
        refreshFeedbacks();
      });
    },
    [candidateId, interviewId, jobId, refreshFeedbacks, setEditUuid, stepId]
  );

  const onPostFeedback = useCallback(
    (data: Partial<FeedbackData>) => {
      const { uuid, ...feedback } = data;

      saveInterviewFeedbacks(
        jobId,
        stepId,
        candidateId,
        uuid as Uuid,
        withFeedbackInterview(getPostFeedbackBody(feedback), interviewId)
      ).then(refreshFeedbacks);
    },
    [candidateId, interviewId, jobId, refreshFeedbacks, stepId]
  );

  const onImproveComment = useCallback(
    (
      body: ImproveInterviewFeedbackCommentPayload,
      signal: AbortSignal
    ): Promise<ImproveInterviewFeedbackCommentResponse> =>
      improveInterviewFeedbackComment(jobId, stepId, candidateId, body, signal),
    [candidateId, jobId, stepId]
  );

  return { onImproveComment, onPostFeedback, onSaveScorecard };
};
